/**
 * The two multiple-choice filters: energy label and neighbourhood. Both show the facet
 * count for each option - the number you would get if you also picked this - and
 * both keep options at zero visible but greyed, so the list never jumps around.
 */
import { buurtNaam, type Energielabel, energielabelKleur, formatAantal } from '@mamimap/shared';
import { useState } from 'react';

type Telling<T> = { waarde: T; aantal: number };

type LabelKeuzeProps = {
  tellingen: readonly Telling<Energielabel | null>[];
  gekozen: readonly Energielabel[];
  /** Woningen zonder label: een eigen groep, want 'geen' is geen label. */
  zonderLabel: boolean;
  onWijzig: (labels: Energielabel[]) => void;
  onWijzigZonder: (aan: boolean) => void;
};

export function LabelKeuze({
  tellingen,
  gekozen,
  zonderLabel,
  onWijzig,
  onWijzigZonder,
}: LabelKeuzeProps) {
  function wissel(label: Energielabel) {
    onWijzig(
      gekozen.includes(label)
        ? gekozen.filter((bestaand) => bestaand !== label)
        : [...gekozen, label],
    );
  }

  const zonder = tellingen.find((telling) => telling.waarde === null);

  // Twaalf labels, een vaste lijst: die past in zijn geheel. Een scrollbalk zou
  // G buiten beeld houden zonder dat iets dat verraadt.
  return (
    <div>
      {tellingen.map((telling) => {
        const label = telling.waarde;
        if (label === null) return null;
        const aan = gekozen.includes(label);
        return (
          <button
            type="button"
            key={label}
            onClick={() => wissel(label)}
            disabled={telling.aantal === 0 && !aan}
            className={`flex w-full items-center gap-2 px-1 py-[3px] text-left text-[12px] disabled:text-faint ${
              aan ? 'bg-accent-soft text-accent-deep' : 'text-ink-soft hover:bg-paper'
            }`}
          >
            <span
              aria-hidden="true"
              className="h-2.5 w-2.5 shrink-0"
              style={{ backgroundColor: energielabelKleur(label) }}
            />
            <span className="w-14">{label}</span>
            <span className="cijfers ml-auto text-[11.5px] text-muted">
              {formatAantal(telling.aantal)}
            </span>
          </button>
        );
      })}

      {/* De facetten tellen de woningen zonder label al; dan hoort die groep ook
          te kiezen te zijn. Hij staat onderaan, na G, met een streep ertussen:
          het is geen dertiende label. */}
      {zonder ? (
        <button
          type="button"
          onClick={() => onWijzigZonder(!zonderLabel)}
          disabled={zonder.aantal === 0 && !zonderLabel}
          className={`mt-1 flex w-full items-center gap-2 border-t border-line px-1 pt-1.5 pb-[3px] text-left text-[12px] disabled:text-faint ${
            zonderLabel ? 'bg-accent-soft text-accent-deep' : 'text-ink-soft hover:bg-paper'
          }`}
        >
          <span
            aria-hidden="true"
            className="h-2.5 w-2.5 shrink-0 border border-line-strong bg-surface-strong"
          />
          <span className="w-14">Geen</span>
          <span className="cijfers ml-auto text-[11.5px] text-muted">
            {formatAantal(zonder.aantal)}
          </span>
        </button>
      ) : null}
    </div>
  );
}

type BuurtKeuzeProps = {
  /** PC4-tellingen; `null` staat voor woningen zonder postcode en valt weg. */
  tellingen: readonly Telling<string | null>[];
  gekozen: readonly string[];
  onWijzig: (buurten: string[]) => void;
};

/**
 * Buurt is de PC4 uit de postcode. Er zijn er tachtig in Amsterdam, dus deze
 * lijst schuift wél - met de gekozen buurten bovenaan, zodat een keuze niet
 * ergens halverwege verdwijnt.
 */
export function BuurtKeuze({ tellingen, gekozen, onWijzig }: BuurtKeuzeProps) {
  const [zoek, zetZoek] = useState('');

  function wissel(buurt: string) {
    onWijzig(
      gekozen.includes(buurt)
        ? gekozen.filter((bestaand) => bestaand !== buurt)
        : [...gekozen, buurt],
    );
  }

  const rijen = tellingen.filter(
    (telling): telling is Telling<string> => typeof telling.waarde === 'string',
  );
  const term = zoek.trim().toLowerCase();
  // Op nummer én op naam: wie 'Gaasperdam' zoekt weet 1108 niet uit zijn hoofd,
  // en wie 1015 intikt wil niet eerst 'Jordaan' hoeven weten. Een gekozen buurt
  // blijft staan, ook als hij buiten de zoekterm valt - anders verdwijnt je eigen
  // keuze zodra je verder typt.
  const gevonden = rijen.filter(
    (telling) =>
      term === '' ||
      gekozen.includes(telling.waarde) ||
      telling.waarde.includes(term) ||
      (buurtNaam(telling.waarde) ?? '').toLowerCase().includes(term),
  );
  const geordend = [
    ...gevonden.filter((telling) => gekozen.includes(telling.waarde)),
    ...gevonden.filter((telling) => !gekozen.includes(telling.waarde)),
  ];

  return (
    <div>
      {/* Zesenzestig regels zijn te veel om langs te schuiven. */}
      {rijen.length > 12 ? (
        <input
          className="veld mb-1"
          type="search"
          aria-label="Buurt zoeken"
          placeholder="Zoek postcode of naam"
          value={zoek}
          onChange={(event) => zetZoek(event.target.value)}
        />
      ) : null}

      <div className="max-h-72 overflow-y-auto">
        {geordend.map((telling) => {
          const aan = gekozen.includes(telling.waarde);
          return (
            <button
              type="button"
              key={telling.waarde}
              onClick={() => wissel(telling.waarde)}
              disabled={telling.aantal === 0 && !aan}
              className={`flex w-full items-center gap-2 px-1 py-[3px] text-left text-[12px] disabled:text-faint ${
                aan ? 'bg-accent-soft text-accent-deep' : 'text-ink-soft hover:bg-paper'
              }`}
            >
              <span className="cijfers shrink-0">{telling.waarde}</span>
              <span className="truncate text-muted">{buurtNaam(telling.waarde) ?? ''}</span>
              <span className="cijfers ml-auto text-[11.5px] text-muted">
                {formatAantal(telling.aantal)}
              </span>
            </button>
          );
        })}
        {geordend.length === 0 ? (
          <p className="px-1 py-1.5 text-[12px] text-muted">Geen buurt met deze naam.</p>
        ) : null}
      </div>
    </div>
  );
}
